import useCurrentTime from "app/player/hooks/useCurrentTime"
import useDuration from "app/player/hooks/useDuration"
import { cn } from "app/lib/utils"
import { forwardRef, useLayoutEffect, useRef, useState } from "react"

const SCRUB_THUMB_SIZE = 32

export type PlayerChipVariant = "dark" | "light"

type ScrubPlaytimeProps = {
  progress: number
  thumbSize?: number
  className?: string
  variant?: PlayerChipVariant
}

function formatSegment(value?: number) {
  if (value === undefined) return "00"
  return Math.floor(value).toLocaleString("en-US", {
    minimumIntegerDigits: 2,
    useGrouping: false,
  })
}

export const playtimeBaseClassName =
  "flex w-fit items-center gap-[3px] rounded-[40px] px-4 py-2 text-md font-semibold whitespace-nowrap text-white select-none"

const playtimeVariantClassName: Record<PlayerChipVariant, string> = {
  dark: "bg-neutraal-opacity-950-65 backdrop-blur-[7.5px]",
  light:
    "bg-white/40 backdrop-blur-[4px] [text-shadow:0_0_2px_rgba(0,0,0,0.25)]",
}

export function getPlaytimeClassName(variant: PlayerChipVariant = "dark") {
  return cn(playtimeBaseClassName, playtimeVariantClassName[variant])
}

export const playtimeClassName = getPlaytimeClassName("dark")

export function PlayerPlaytimeContent() {
  const duration = useDuration()
  const currentTime = useCurrentTime()

  return (
    <>
      <span>
        {currentTime?.minutes ?? 0}:{formatSegment(currentTime?.seconds)}
      </span>
      <span>/</span>
      <span>
        {duration?.minutes ?? 0}:{formatSegment(duration?.seconds)}
      </span>
    </>
  )
}

export const PlayerStaticPlaytime = forwardRef<
  HTMLDivElement,
  { className?: string; variant?: PlayerChipVariant }
>(function PlayerStaticPlaytime({ className, variant = "dark" }, ref) {
  return (
    <div ref={ref} className={cn(getPlaytimeClassName(variant), className)}>
      <PlayerPlaytimeContent />
    </div>
  )
})

export function PlayerScrubPlaytime({
  progress,
  thumbSize = SCRUB_THUMB_SIZE,
  className,
  variant = "dark",
}: ScrubPlaytimeProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [halfWidth, setHalfWidth] = useState(0)
  const halfThumb = thumbSize / 2
  const thumbCenter = `calc((100% - ${thumbSize}px) * ${progress} + ${halfThumb}px)`

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return

    const update = () => setHalfWidth(el.offsetWidth / 2)
    update()

    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      className={cn(
        getPlaytimeClassName(variant),
        "pointer-events-none absolute bottom-full z-10 -translate-x-1/2",
        className
      )}
      style={{
        left:
          halfWidth > 0
            ? `clamp(${halfWidth}px, ${thumbCenter}, calc(100% - ${halfWidth}px))`
            : thumbCenter,
      }}
    >
      <PlayerPlaytimeContent />
    </div>
  )
}
